import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-37">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-navy/80 backdrop-blur-sm z-50" onClick={onClose} data-id="element-38" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} onClick={onClose} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-navy-light rounded-2xl shadow-2xl border border-white/10 z-50 flex flex-col overflow-hidden cursor-pointer" data-id="element-39">
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-40">
              <h2 className="text-xl font-bold font-serif text-cream" data-id="element-41">
                {title}
              </h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors" data-id="element-42">
                <X className="w-5 h-5 text-cream/60" data-id="element-43" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-cream/80 text-sm leading-relaxed space-y-4" data-id="element-44">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="mt-20 pt-8 pb-6 border-t border-white/10 max-w-4xl mx-auto relative z-10" data-id="element-45">
        <p className="text-center text-xs text-cream/50 mb-4" data-id="element-46">
          This guide is for educational purposes only. It is not medical or
          psychological advice.
        </p>
        <div className="flex items-center justify-center gap-6 mb-4" data-id="element-47">
          <button onClick={() => setActiveModal('privacy')} className="text-xs text-cream/50 hover:text-gold transition-colors underline underline-offset-2" data-id="element-48">
            Privacy Policy
          </button>
          <span className="text-cream/20" data-id="element-49">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-xs text-cream/50 hover:text-gold transition-colors underline underline-offset-2" data-id="element-50">
            Terms of Service
          </button>
        </div>
        <p className="text-center text-xs text-cream/40" data-id="element-51">
          © {new Date().getFullYear()} GraceLife by GoDeskCore. All rights
          reserved.
        </p>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-52">
        <p className="font-semibold text-cream" data-id="element-53">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })}
        </p>

        <h3 className="font-bold text-cream mt-4" data-id="element-54">1. Information We Collect</h3>
        <p data-id="element-55">
          When you purchase "Is This God or My Fear? — Relationship Edition," we
          collect only the information necessary to process your order,
          including your name, email address, and payment details. Payment
          processing is handled securely by our third-party payment processor.
        </p>

        <h3 className="font-bold text-cream" data-id="element-56">2. How We Use Your Information</h3>
        <p data-id="element-57">We use your information solely to:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-58">
          <li data-id="element-59">Deliver your digital product via email</li>
          <li data-id="element-60">Process refund requests within the 30-day guarantee period</li>
          <li data-id="element-61">Send purchase confirmations and product updates (if opted in)</li>
        </ul>

        <h3 className="font-bold text-cream" data-id="element-62">3. Data Sharing</h3>
        <p data-id="element-63">
          We do not sell, rent, or share your personal information with third
          parties for marketing purposes. Your data is only shared with our
          payment processor and email delivery service as necessary to fulfill
          your order.
        </p>

        <h3 className="font-bold text-cream" data-id="element-64">4. Data Security</h3>
        <p data-id="element-65">
          We implement industry-standard security measures to protect your
          personal information. All transactions are encrypted using SSL
          technology.
        </p>

        <h3 className="font-bold text-cream" data-id="element-66">5. Cookies</h3>
        <p data-id="element-67">
          Our website may use cookies to improve your browsing experience and
          analyze site traffic. You can disable cookies in your browser settings
          at any time.
        </p>

        <h3 className="font-bold text-cream" data-id="element-68">6. Your Rights</h3>
        <p data-id="element-69">
          You have the right to request access to, correction of, or deletion of
          your personal data. To exercise these rights, please reach out through
          the platform where you made your purchase.
        </p>

        <h3 className="font-bold text-cream" data-id="element-70">7. Changes to This Policy</h3>
        <p data-id="element-71">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service" data-id="element-72">
        <p className="font-semibold text-cream" data-id="element-73">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })}
        </p>

        <h3 className="font-bold text-cream mt-4" data-id="element-74">1. Product Description</h3>
        <p data-id="element-75">
          "Is This God or My Fear? — Relationship Edition" is a digital
          educational toolkit delivered as a PDF. It is designed for personal
          clarity and decision-making. It is not therapy, counseling, medical
          advice, or a substitute for professional support.
        </p>

        <h3 className="font-bold text-cream" data-id="element-76">2. Digital Delivery</h3>
        <p data-id="element-77">
          Upon purchase, you will receive immediate access to download the
          complete PDF toolkit. Due to the digital nature of this product,
          delivery is instant and cannot be "returned" in the traditional sense.
        </p>

        <h3 className="font-bold text-cream" data-id="element-78">3. Refund Policy</h3>
        <p data-id="element-79">
          We offer a 30-day money-back guarantee. If you are not satisfied with
          the product, you may request a full refund within 30 days of purchase
          by contacting us through the platform where you made your purchase. No
          questions asked.
        </p>

        <h3 className="font-bold text-cream" data-id="element-80">4. Intellectual Property</h3>
        <p data-id="element-81">
          All content within this toolkit — including the 3-Filter Discernment
          Framework, scripts, templates, and written materials — is the
          intellectual property of GraceLife by GoDeskCore. You may not
          reproduce, distribute, or resell any part of this product without
          written permission.
        </p>

        <h3 className="font-bold text-cream" data-id="element-82">5. Personal Use License</h3>
        <p data-id="element-83">
          Your purchase grants you a personal, non-transferable license to use
          the toolkit for your own decision-making. You may print copies for
          personal use. You may not share digital copies or distribute the
          materials to others.
        </p>

        <h3 className="font-bold text-cream" data-id="element-84">6. Disclaimer</h3>
        <p data-id="element-85">
          This product is for educational and informational purposes only.
          GraceLife by GoDeskCore is not responsible for any decisions made as a
          result of using this toolkit. If you are in crisis or experiencing a
          safety concern, please seek professional help immediately.
        </p>

        <h3 className="font-bold text-cream" data-id="element-86">7. Limitation of Liability</h3>
        <p data-id="element-87">
          To the fullest extent permitted by law, GraceLife by GoDeskCore shall
          not be liable for any indirect, incidental, or consequential damages
          arising from the use of this product.
        </p>

        <h3 className="font-bold text-cream" data-id="element-88">8. Governing Law</h3>
        <p data-id="element-89">
          These terms shall be governed by and construed in accordance with
          applicable laws. Any disputes shall be resolved through the
          appropriate legal channels.
        </p>
      </Modal>
    </>;
}